import {
  Box,
  Button,
  Dialog,
  Backdrop,
  TextField,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ShortBook } from 'library/types';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import { GridRowId } from '@mui/x-data-grid-premium';
import { renderAutoCompleteLocaleText } from 'utils';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { FormEvent, useCallback, useEffect, useState } from 'react';

export type AssignProjectPostParams = {
  userId: number;
  bookIds: number[];
};

type AssignProjectProps = {
  open: boolean;
  userId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const AssignProject = ({ open, userId, handleClose: closeDialog, refresh }: AssignProjectProps) => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState<ShortBook[]>([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState<ShortBook[]>([]);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const postData: AssignProjectPostParams = {
      userId: userId as number,
      bookIds: selectedBooks.map((selectedBook) => selectedBook.id),
    };

    try {
      setLoading(true);

      const response = await apiService.assignProject(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="users.assignProjectCompleted"
            defaultMessage="Proje atama işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getBooks = async () => {
    try {
      setLoadingBooks(true);

      const response = await apiService.getCompanies();

      if (response.isSuccess) {
        setBooks(response.data);
      }
    } catch {
    } finally {
      setLoadingBooks(false);
    }
  };

  const getSpecificUser = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificUser(userId as number);

      if (response.isSuccess) {
        let sBooks: ShortBook[] = [];

        response.data.bookIds.forEach((bookId) => {
          const book = books.find((book) => book.id === bookId);

          if (book) {
            sBooks.push(book);
          }
        });

        setSelectedBooks(sBooks);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [books, userId]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setSelectedBooks([]);
  };

  // useEffect hooks
  useEffect(() => {
    if (open) {
      getBooks();
    }
  }, [open]);

  useEffect(() => {
    if (open && books.length) {
      getSpecificUser();
    }
  }, [open, books, getSpecificUser]);

  return (
    <Dialog maxWidth="xl" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="users.assignProject" defaultMessage="Proje Ata" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="users.assignProjectText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili kullanıcı için atama yapabilirsiniz."
            />
          </DialogContentText>
          <Autocomplete
            size="small"
            multiple
            options={books}
            loading={loadingBooks}
            value={selectedBooks}
            filterSelectedOptions
            disableCloseOnSelect
            {...renderAutoCompleteLocaleText()}
            onChange={(_, newValue: ShortBook[]) => {
              setSelectedBooks(newValue);
            }}
            getOptionLabel={(option: ShortBook) => option.title}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="users.books" defaultMessage="Projeler" />}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBooks ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default AssignProject;
